import {BrowserStorage} from "../../../storage/BrowserStorage";
import {Cart} from "../../../typescript-library";
import {ICartContext, ICartContextNoCart, isCart} from "../../cart/CartContextProvider/CartContextProvider";
import {PlaceType} from "./paypal/helpers/paypalTypes";

type ItemPositionsType = {itemId: number; quantity: number}[];
export type LocalStorageDataType = {itemPos?: ItemPositionsType; voucherPos?: string[]; itemId?: number};

export const cartStorage = new BrowserStorage<LocalStorageDataType>({
  type: "localStorage",
  dataIdentifier: "cart",
  hoursToCache: 24 * 30,
});
export const cartContentKey = "content";

/**
 * Decides whether to use the current cart, or to retrieve one and to fill it with data from pdp
 * @param place
 * @param context
 * @param itemId
 */
export async function decideCartContent(
  place: PlaceType,
  context: ICartContext | ICartContextNoCart,
  itemId: number | undefined,
) {
  let tempCart: Cart;
  if (place === "cart") {
    if (isCart(context)) {
      tempCart = context.cart;
    } else {
      throw Error("No cart on context");
    }
  } else {
    // place === "pdp"
    if (!itemId) throw Error("No itemId on #AddToCartButtonRoot");
    if (isCart(context)) {
      await swapCart(context.cart, itemId);
      tempCart = context.cart;
    } else {
      // get cart and add that one item
      // (maybe no restore needed)
      tempCart = await context.requestCart().then(async ({cart, done}) => {
        await cart.addItem(itemId, 1).then(done);

        return cart;
      });
    }
  }

  if (!tempCart) {
    throw Error("No cart on context");
  }

  return tempCart;
}

/**
 * Clean and cache cart content to localStorage, and just add that one item
 * @param cart Cart to cache and clean
 * @param itemId Item to add
 */
async function swapCart(cart: Cart, itemId: number) {
  const {positions, rebates} = cart;

  const itemPositions = positions
    .filter((pos) => pos.type === "Item")
    .reduce<ItemPositionsType>((arr, curr) => {
      if (curr.reference?.itemId) arr.push({itemId: curr.reference.itemId, quantity: curr.quantity});

      return arr;
    }, []);

  let voucherPositions = positions
    .filter((pos) => pos.type === "Voucher")
    .reduce<string[]>((arr, curr) => {
      if (curr.reference?.voucher?.code) {
        arr.push(curr.reference.voucher.code);
      }

      return arr;
    }, []);

  if (rebates.length) {
    voucherPositions = voucherPositions.concat(rebates.map((rebate) => rebate.code));
  }

  const cartInfo: LocalStorageDataType = {};

  if (itemPositions.length) {
    for (const itemPosition of itemPositions) {
      if (itemPosition.itemId !== itemId) {
        await cart.removeItem(itemPosition.itemId);
      }
    }
    cartInfo.itemPos = itemPositions;
  }

  if (voucherPositions.length) {
    for (const voucherPosition of voucherPositions) {
      await cart.removeVoucher(voucherPosition);
    }
    cartInfo.voucherPos = voucherPositions;
  }

  if (Object.keys(cartInfo).length) {
    cartInfo.itemId = itemId;
    cartStorage.write(cartContentKey, cartInfo);
  }
  await cart.addItem(itemId, 1);
}

/**
 * Restore cart on successful purchase or on cancel/error
 * @param cart cart to alter
 */
export async function restoreCart(cart: Cart) {
  const cartContent = cartStorage.read(cartContentKey);
  if (!cartContent) return;

  if (cartContent.itemId && !cartContent.itemPos?.some((ip) => ip.itemId === cartContent.itemId)) {
    await cart.removeItem(cartContent.itemId);
  }
  for (const position of cart.positions) {
    const voucherCode = position.reference?.voucher?.code;
    if (position.type === "Voucher" && !!voucherCode && !cartContent.voucherPos?.some((vp) => vp === voucherCode)) {
      await cart.removeVoucher(voucherCode);
    }
  }
  for (const rebate of cart.rebates) {
    await cart.removeVoucher(rebate.code);
  }

  if (cartContent.itemPos) {
    await cart.addMultipleItems(cartContent.itemPos.map((itm) => ({...itm, gtin: null})));
  }
  if (cartContent.voucherPos) {
    for (const voucherPos of cartContent.voucherPos) {
      if (!cart.positions.some((pos) => pos.reference?.voucher?.code === voucherPos)) {
        await cart.addVoucher(voucherPos);
      }
    }
  }
  cartStorage.remove(cartContentKey);
}
